import React, { createContext, useContext, useState } from "react";

export const ROLES = {
  OWNER: "Owner / Admin",
  MANAGER: "Warehouse Manager",
  WAREHOUSE: "Warehouse Staff",
  SALES: "Sales Team",
  FINANCE: "Accountant / Finance",
  RETAIL: "Retail Shop Staff"
};

const DEFAULT_USERS = [
  {
    id: "U-1",
    name: "Narayan Rao Hegde",
    initials: "NH",
    email: "narayan.hegde@kiranastore.com",
    role: ROLES.OWNER,
    color: "from-emerald-600 to-teal-500",
    accessibleWarehouses: ["WH-001", "WH-002", "WH-003"]
  },
  {
    id: "U-2",
    name: "Venkateshwar Reddy",
    initials: "VR",
    email: "venkat.reddy@kiranastore.com",
    role: ROLES.MANAGER,
    color: "from-sky-600 to-blue-500",
    accessibleWarehouses: ["WH-001", "WH-002"]
  },
  {
    id: "U-3",
    name: "Vikas Goud",
    initials: "VG",
    email: "vikas.goud@kiranastore.com",
    role: ROLES.WAREHOUSE,
    color: "from-amber-600 to-orange-500",
    accessibleWarehouses: ["WH-001"]
  },
  {
    id: "U-4",
    name: "Rameshwar Goud",
    initials: "RG",
    email: "rameshwar.sales@kiranastore.com",
    role: ROLES.SALES,
    color: "from-indigo-600 to-purple-500",
    accessibleWarehouses: ["WH-001", "WH-002", "WH-003"]
  },
  {
    id: "U-5",
    name: "Swathi Kulkarni",
    initials: "SK",
    email: "swathi.finance@kiranastore.com",
    role: ROLES.FINANCE,
    color: "from-purple-600 to-pink-500",
    accessibleWarehouses: ["WH-001", "WH-002", "WH-003"]
  },
  {
    id: "U-6",
    name: "Anand Deshmukh",
    initials: "AD",
    email: "anand.deshmukh@kiranastore.com",
    role: ROLES.RETAIL,
    color: "from-emerald-600 to-green-500",
    accessibleWarehouses: ["WH-003"]
  }
];

// Role-to-module permission map
export const ROLE_PERMISSIONS = {
  [ROLES.OWNER]: ["*"],
  [ROLES.MANAGER]: [
    "dashboard",
    "procurement",
    "receiving",
    "inventory",
    "warehouse",
    "stockTransfer",
    "suppliers",
    "batchExpiry",
    "deadStock",
    "returns",
    "analytics",
    "notifications",
    "auditLog",
    "settings"
  ],
  [ROLES.WAREHOUSE]: [
    "dashboard",
    "receiving",
    "inventory",
    "warehouse",
    "stockTransfer",
    "batchExpiry",
    "returns",
    "notifications"
  ],
  [ROLES.SALES]: [
    "dashboard",
    "inventory",
    "sales",
    "customers",
    "stockTransfer",
    "analytics",
    "notifications"
  ],
  [ROLES.FINANCE]: [
    "dashboard",
    "procurement",
    "sales",
    "finance",
    "customers",
    "suppliers",
    "employees",
    "analytics",
    "auditLog",
    "notifications"
  ],
  [ROLES.RETAIL]: [
    "dashboard",
    "sales",
    "inventory",
    "warehouse",
    "returns",
    "customers",
    "notifications"
  ]
};

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const saved = localStorage.getItem("kirana_current_user");
      if (saved) {
        const parsed = JSON.parse(saved);
        const matched = DEFAULT_USERS.find((u) => u.id === parsed.id);
        if (matched) return matched;
      }
    } catch (e) {
      console.warn("Error reading current user from storage", e);
    }
    return DEFAULT_USERS[0];
  });

  const switchUser = (userId) => {
    const found = DEFAULT_USERS.find((u) => u.id === userId) || DEFAULT_USERS[0];
    setCurrentUser(found);
    try {
      localStorage.setItem("kirana_current_user", JSON.stringify(found));
    } catch (e) {
      console.warn("Error saving current user to storage", e);
    }
  };

  const hasPermission = (moduleKey) => {
    if (!currentUser) return false;
    const allowed = ROLE_PERMISSIONS[currentUser.role] || [];
    if (allowed.includes("*")) return true;
    return allowed.includes(moduleKey);
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        availableUsers: DEFAULT_USERS,
        switchUser,
        hasPermission,
        role: currentUser?.role || ROLES.OWNER
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
